<script setup lang="ts">
import { calculateValidity, stringToFaDateTime } from '@payjib/shared'
import { useQuery } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { deleteRequisitionAction } from '@/api/functions/requisitions/delete-requisition-action'
import { getRequisitionDetailAction } from '@/api/functions/requisitions/get-requisition-detail-action'
import {
  getRequisitionOffersAction,
  type RequisitionOffer,
} from '@/api/functions/requisitions/get-requisition-offers.action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import { numberWithCommas } from '@/utils/number-with-commas'
import OfferCard from '../offers/partials/OfferCard.vue'
import UpdateRequestModal from './partials/UpdateRequestModal.vue'

const router = useRouter()
const route = useRoute()
const toast = useToast()

const requestId = ref(route.params.id as string)
const showDeleteModal = ref(false)
const showEditModal = ref(false)
const routeQuery = computed(() => route.query)
const currentPage = ref(1)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const activeFilters = ref<Record<string, any>>({})

const { offersStatusOptions, currencies, countries, transferMethods } = useGeneralSettings()

// Fetch Requisition Detail
const { data: requisitionDetailData, refetch: refetchRequest } = useQueryApi({
  queryKey: ['requisitionDetail', requestId],
  queryFn: () => getRequisitionDetailAction(requestId.value),
  enabled: !!requestId.value,
  staleTime: 1000 * 60 * 5,
})

const request = computed(() => requisitionDetailData.value?.data || null)

// Fetch Requisition Offers
const { data: requisitionOffersData, isLoading: isOffersLoading } = useQueryApi({
  queryKey: computed(() => ['requisitionOffers', requestId.value, routeQuery.value]),
  queryFn: () =>
    getRequisitionOffersAction(requestId.value, {
      page: Number(routeQuery.value.page) || 1,
      ...routeQuery.value, // بقیه فیلترها
    }),
  enabled: !!requestId.value,
  // keepPreviousData: true,

  staleTime: 1000 * 60 * 3,
})

const offers = computed<RequisitionOffer[]>(() => requisitionOffersData.value?.data || [])

// Pagination Computed
const pagination = computed(() => requisitionOffersData.value?.pagination)
const totalItems = computed(() => pagination.value?.total || 0)
const itemsPerPage = computed(() => pagination.value?.perPage || 5)

// Filters
const filterFields = [
  {
    type: 'select',
    key: 'status',
    label: 'انتخاب وضعیت پیشنهاد',
    title: 'وضعیت پیشنهاد',
    options: offersStatusOptions.value,
  },
]

// --- Filters Events ---
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onApplyFilters = (values: Record<string, any>) => {
  const cleanFilters = Object.fromEntries(
    Object.entries(values).filter(([, v]) => v !== '' && v !== null && v !== undefined)
  )

  router.replace({
    query: {
      ...cleanFilters,
      page: '1',
    },
  })
}

const onResetFilters = () => {
  activeFilters.value = {}
  currentPage.value = 1

  router.replace({
    query: { page: '1' },
  })
}

// Pagination Methods
const goToPage = (page: number) => {
  router.replace({
    query: {
      ...route.query,
      page: String(page),
    },
  })
}

// Delete Requisition
const deleteMutation = useMutationApi({
  mutationFn: (id: string) => deleteRequisitionAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'درخواست با موفقیت حذف شد')
    router.push('/requisitions')
    showDeleteModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleDeleteRequisition = () => {
  if (!requestId.value) return
  deleteMutation.mutate(requestId.value)
}

const handleRequestUpdated = () => {
  refetchRequest()
}

watch([pagination], () => {
  if (currentPage.value > (pagination.value?.lastPage || 1)) {
    currentPage.value = pagination.value?.lastPage || 1
  }
})

watch(
  routeQuery,
  (newQuery) => {
    currentPage.value = Number(newQuery.page) || 1
    activeFilters.value = { status: newQuery.status || '' } // سایر فیلترها هم اضافه کن
  },
  { immediate: true }
)
</script>

<template>
  <div class="mb-9">
    <ConfirmModal v-model="showDeleteModal" title="حذف درخواست" :message="'آیا از حذف این درخواست اطمینان دارید؟'"
      confirm-text="بله حذف شود" cancel-text="بازگشت" @confirm="handleDeleteRequisition"
      @cancel="showDeleteModal = false" />

    <UpdateRequestModal v-model="showEditModal" :request="request as any" :currencies="currencies"
      :countries="countries" :transferMethods="transferMethods" @confirm="handleRequestUpdated"
      @cancel="showEditModal = false" />

    <router-link to="/requisitions"
      class="w-fit mb-6 gap-1 flex items-center h-10 rounded-lg text-[#2d5396] px-5 hover:bg-[#D5E2F1] text-[13px]">
      <img src="/svg/back-icon.svg" class="me-1" width="22" />
      <span>بازگشت</span>
    </router-link>

    <div class=" flex flex-col lg:flex-row lg:items-center justify-between mb-9">

      <div class="w-full lg:w-auto flex flex-col lg:flex-row lg:items-center gap-2 ">
        <div class="text-[#1b325a] text-[22px]">
          درخواست شماره <span class="font-fa">{{ request?.code }}</span>
        </div>

        <div class="w-full lg:w-auto flex flex-row items-center justify-between lg:justify-start gap-2">

          <!-- status  -->
          <div class="w-fit flex items-center justify-center text-[13px] font-medium h-10 rounded-[10px] px-5 me-1"
            :class="request?.status === 'PENDING' ? 'bg-[#fff8e7] text-[#ffba0e] ' :
              request?.status === 'IN_TRANSACTION' ? 'bg-[#d5ddea] text-[#2d5396] ' :
                request?.status === 'FINISHED' ? 'bg-[#e6fff0] text-[#2ed06e] ' :
                  request?.status === 'DELETEDBYUSER' ? 'bg-[#e6fff0] text-[#2ed06e] ' :
                    request?.status === 'DELETEDBYADMIN' ? 'bg-[#e6fff0] text-[#2ed06e] ' :
                      'bg-[#fde7ef] text-[#ed145b] '">
            <span>
              {{ request?.status === 'PENDING' ? 'درحال انتظار' :
                request?.status === 'IN_TRANSACTION' ? 'در حال انجام' :
                  request?.status === 'FINISHED' ? 'پایان یافته' :
                    request?.status === 'DELETEDBYUSER' ? 'حذف شده توسط کاربر' :
                      request?.status === 'DELETEDBYADMIN' ? 'حذف شده توسط ادمین' :
                        request?.status === 'CANCELLED' ? 'لغو شده' : 'منقضی شده' }}
            </span>
          </div>

          <!-- discount  -->
          <div v-if="request?.discount?.currentUser"
            class=" w-fit flex items-center justify-center text-[13px] bg-[#f3d0f2] text-[#8c228a] font-medium h-10 rounded-[10px] px-5">
            دارای تخفیف
          </div>
        </div>
        <!-- edit & delete  -->
        <div v-if="request?.status === 'PENDING'" class="w-full lg:w-auto flex flex-row items-center gap-2">
          <Button :isAdmin="true" @click="showEditModal = true"
            class=" w-1/2 lg:w-auto px-2  rounded-[10px]  h-10! py-0 text-[13px] font-normal ">
            <span class="text-nowrap flex items-center  justify-center gap-2 ">
              <img width="20" src="/svg/edit-icon.svg" />
              ویرایش درخواست
            </span>
          </Button>
          <Button @click="showDeleteModal = true" :isAdmin="true" variant="outline"
            class="w-1/2 lg:w-auto px-1.5  border-2  rounded-[10px]  h-10! py-0 text-[13px] font-normal ">
            <span class="flex items-center justify-center gap-2 ">
              <img width="19" src="/svg/trash-icon2.svg" />
              حذف درخواست
            </span>
          </Button>
        </div>

      </div>
      <div class="text-[#2d5396] text-sm mt-4 lg:mt-0">
        زمان باقیمانده اعتبار: <span class="font-fa text-[#1b325a ]"> {{ request?.expiresAt ?
          calculateValidity(request?.expiresAt) : '-'
        }}</span>
      </div>

    </div>

    <div class="w-full flex flex-col  p-5 lg:p-8 bg-white border border-[#d8d8d8] rounded-xl">
      <div class="flex flex-col lg:flex-row justify-between gap-6 lg:gap-[168.73px]">
        <div class="w-full lg:w-1/2 flex flex-col gap-3">
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">تاریخ ثبت:</span>
            <span dir="ltr" class="font-fa text-[#1b325a]">{{ request?.createdAt ?
              stringToFaDateTime(request?.createdAt) : '-'
            }}</span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">درخواست کننده:</span>
            <span class="text-[#1b325a]">
              <RouterLink v-if="request?.owner?.id" :to="`/users/${request.owner.uuid}`"
                class="text-[#2d5396] hover:underline">
                {{ request?.owner?.fullName }}
              </RouterLink>
              <span v-else>{{ request?.owner?.fullName }}</span>
            </span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">نوع درخواست:</span>
            <span class=" text-[#1b325a]">{{ request?.type === 'BUY' ? 'خرید' : 'فروش' }}</span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">کشور:</span>

            <div v-if="request?.type === 'SELL'" class="flex items-center gap-1 text-sm font-medium text-[#000000]">
              <img :src="request?.bankAccountCountries?.seller?.flagUrl" width="24" height="16" alt="flag" />
              {{ request?.bankAccountCountries?.seller?.title?.fa }}
            </div>

            <div v-if="request?.type === 'BUY'" class="flex items-center gap-1">

              <div v-if="request?.bankAccountCountries?.internationalBuyer"
                class="flex items-center gap-1 text-sm font-medium text-[#000000]">
                <img :src="request?.bankAccountCountries?.internationalBuyer?.flagUrl" width="24" height="16"
                  alt="flag" />
                {{ request?.bankAccountCountries?.internationalBuyer?.title?.fa }}
              </div>
              <div v-if="request?.bankAccountCountries?.internationalPaypalBuyer
              " class="flex items-center gap-1 text-sm font-medium text-[#000000]">
                <img :src="request?.bankAccountCountries?.internationalPaypalBuyer?.flagUrl" width="22" height="16"
                  alt="flag" />
                {{ request?.bankAccountCountries?.internationalPaypalBuyer?.title?.fa }}
              </div>
              <div v-if="
                request?.bankAccountCountries?.internationalRevolutBuyer"
                class="flex items-center gap-1 text-sm font-medium text-[#000000]">
                <img :src="request?.bankAccountCountries?.internationalRevolutBuyer?.flagUrl" width="22" height="16"
                  alt="flag" />
                {{ request?.bankAccountCountries?.internationalRevolutBuyer?.title?.fa }}
              </div>
            </div>


          </div>
        </div>
        <div class="w-full lg:w-1/2 flex flex-col gap-3">
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">ارز:</span>
            <span class="font-fa text-[#1b325a]">{{ request?.currency?.titleFa }}</span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">مقدار:</span>
            <span class="font-fa text-[#1b325a]">{{ numberWithCommas(request?.currencyAmount) }}</span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">نرخ تبدیل:</span>
            <span class="font-fa">{{ request?.exchangeRate?.toLocaleString() }} تومان</span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">مبلغ با احتساب کارمزد:</span>
            <span class="font-fa">{{ request?.finalRialAmount?.toLocaleString() }} تومان</span>
          </div>
          <div v-if="request?.discount?.currentUser?.rate" class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]"> تخفیف:</span>
            <span class="font-fa">{{ request?.discount?.currentUser?.rate }}%</span>
          </div>
        </div>
      </div>
      <!-- Description -->
      <div v-if="request?.description"
        class="border-t border-[#eaeef5] pt-4 text-[13px] text-[#4e648c] font-medium  mt-5">
        توضیحات: {{ request?.description }}
      </div>
    </div>


    <div class="text-[#1b325a] text-[22px] mt-12 mb-6">
      پیشنهادهای ثبت شده
    </div>

    <!-- 👉 Filters Section -->
    <Filters :fields="filterFields" @apply="onApplyFilters" @reset="onResetFilters" class="mt-8 mb-6" />

    <!-- 👉 Offers list and pagination -->
    <div v-if="isOffersLoading" class="text-center py-8 text-gray-500">
      در حال دریافت اطلاعات...
    </div>
    <div v-else>
      <div v-if="offers.length > 0" class="mt-6 flex flex-col gap-6 lg:gap-6">
        <OfferCard v-for="(offer, index) in offers" :key="`card-item-${index}`" :offer="offer"
          @deleted="() => console.log('درخواست حذف شد')" />

        <!-- Pagination -->
        <Pagination v-model:currentPage="currentPage" :itemsPerPage="itemsPerPage" :totalItems="totalItems"
          @update:currentPage="goToPage" :isAdmin="true" />

      </div>
      <EmptyState v-else :onReset="onResetFilters" />
    </div>
  </div>
</template>
